import type {
  AiOperationsCurrentProposal,
  AiOperationsProposalRound,
  AiOperationsRun,
  ApprovalProposalDetail,
} from "@adpilot/shared";
import { IconX } from "@tabler/icons-react";
import { useTranslation } from "react-i18next";
import { ControlButton } from "@/components/patterns/control-button";
import { Badge } from "@/components/ui/badge";
import { Sheet, SheetContent, SheetDescription, SheetTitle } from "@/components/ui/sheet";
import { useFormatters } from "@/lib/format";
import { Fact } from "./DecisionRecordPrimitives";

export function RunDetailsDrawer({
  onOpenChange,
  open,
  proposal,
  review,
  rounds,
  run,
}: {
  onOpenChange: (open: boolean) => void;
  open: boolean;
  proposal?: AiOperationsCurrentProposal;
  review?: ApprovalProposalDetail;
  rounds: AiOperationsProposalRound[];
  run: AiOperationsRun;
}): React.ReactElement {
  const { t } = useTranslation("aiOperations");
  const { formatDateTime, formatPercent } = useFormatters();
  const artifact = proposal?.artifact ?? review?.artifact;
  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        className="w-[420px] max-w-[420px] gap-0 rounded-none data-[side=right]:sm:max-w-[420px]"
        showCloseButton={false}
      >
        <div className="border-b border-line px-5 py-5">
          <div className="flex items-start justify-between gap-3">
            <div>
              <SheetTitle className="text-lg">
                {t("runDetail.decisionRecord.details.title")}
              </SheetTitle>
              <SheetDescription className="mt-1 text-xs">
                {t("runDetail.decisionRecord.details.description")}
              </SheetDescription>
            </div>
            <ControlButton
              controlSize="icon-compact"
              variant="outline"
              aria-label={t("runDetail.decisionRecord.details.close")}
              onClick={() => onOpenChange(false)}
            >
              <IconX />
            </ControlButton>
          </div>
        </div>
        <div className="paper-scrollbar flex min-h-0 flex-1 flex-col gap-5 overflow-y-auto px-5 py-5">
          <DetailSection title={t("runDetail.decisionRecord.details.run")}>
            <Fact label={t("runDetail.context.job")} value={t(`runDetail.job.${run.jobType}`)} />
            <Fact
              label={t("runDetail.context.status")}
              value={t(`runDetail.status.${run.status}`)}
            />
            <Fact label={t("runDetail.context.started")} value={formatDateTime(run.startedAt)} />
            <Fact
              label={t("runDetail.context.completed")}
              value={run.finishedAt ? formatDateTime(run.finishedAt) : "—"}
            />
            <Fact
              label={t("runDetail.decisionRecord.details.trigger")}
              value={
                run.triggerType === "manual"
                  ? t("runDetail.trigger.manual")
                  : t("runDetail.trigger.scheduled")
              }
            />
            <Fact
              label={t("runDetail.decisionRecord.details.runId")}
              value={run.id.slice(0, 8).toUpperCase()}
              mono
            />
          </DetailSection>
          {artifact ? (
            <DetailSection title={t("runDetail.decisionRecord.details.policy")}>
              <Fact
                label={t("runDetail.record.policy.goal")}
                value={t(`policy.goal.${artifact.validation.effectivePolicy.businessGoal}`)}
              />
              <Fact
                label={t("runDetail.record.policy.targetAcos")}
                value={formatPercent(artifact.validation.effectivePolicy.targetAcos)}
              />
              <Fact
                label={t("runDetail.record.audit.attempt")}
                value={`${artifact.validation.candidateAttempt}/3`}
                mono
              />
              <Fact
                label={t("runDetail.record.policy.snapshot")}
                value={artifact.validation.policySnapshotId.slice(0, 8).toUpperCase()}
                mono
              />
            </DetailSection>
          ) : null}
          <DetailSection title={t("runDetail.decisionRecord.details.rounds")}>
            {rounds.map((round) => (
              <div
                key={round.id}
                className="flex items-center justify-between rounded-[8px] border border-line bg-subtle/35 px-3 py-2.5"
              >
                <div>
                  <p className="text-xs font-semibold text-ink">
                    {t("runDetail.rounds.round", { number: round.roundNumber })}
                  </p>
                  <p className="mt-0.5 text-[10px] text-muted-foreground">
                    {t("runDetail.rounds.counts", {
                      actions: round.actionCount,
                      conclusions: round.conclusionCount,
                    })}
                  </p>
                </div>
                <Badge variant={round.closedReason ? "neutral" : "outline"}>
                  {round.closedReason
                    ? t("runDetail.rounds.revisionRequested")
                    : t("runDetail.rounds.current")}
                </Badge>
              </div>
            ))}
          </DetailSection>
        </div>
      </SheetContent>
    </Sheet>
  );
}

function DetailSection({
  children,
  title,
}: {
  children: React.ReactNode;
  title: string;
}): React.ReactElement {
  return (
    <section>
      <h3 className="mb-2.5 text-xs font-semibold uppercase tracking-[0.04em] text-muted-foreground">
        {title}
      </h3>
      <dl className="flex flex-col gap-2">{children}</dl>
    </section>
  );
}
